import type { EngagementCandidate, EngagementResolution } from '@clockwise/shared';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Combobox } from './combobox';
import { EngagementLabel, type EngagementTone, engagementDisplay } from './engagement-label';

const GROUP_MATCHING = 'matching';
const GROUP_OTHER = 'other';

interface Props {
  /** What the rule says about this row — see `resolveRowEngagement`. */
  resolution: EngagementResolution;
  /** Engagements that list the row's activity, or that list no
   *  activities at all. The dropdown's first section. */
  candidates: readonly EngagementCandidate[];
  /**
   * Every engagement active for the row's customer over its dates, matching or
   * not. The dropdown's second section, and the reason the field is editable
   * even when nothing matches: an engagement the activity isn't listed on is a
   * legitimate thing to record (that is what "out of scope" means), and the
   * server allows it. Without this the zero-match row is a dead end — the
   * customer plainly has engagements and the UI offers none of them.
   */
  activeEngagements: readonly EngagementCandidate[];
  /** The row is status- or period-locked: show the binding, refuse to edit it. */
  readOnly?: boolean;
  /** Submit-time field error for this row's engagement, if the last submit
   *  flagged one. */
  error?: string;
  /**
   * Whether the INLINE picker is open. This is the change path — the user
   * clicking the engagement to swap it. The forced ask (2+ engagements cover a
   * newly chosen activity) is a dialog the grid owns instead; see
   * `TimesheetEngagementDialog`.
   */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (engagementId: string) => void;
}

/**
 * The engagement field beneath a week-grid row's customer.
 *
 * A FIELD, not a prompt. From the moment the row has a customer and an
 * activity it is always visible and always editable, showing whatever the rule
 * resolved as its value — so attribution reads as "here is the engagement,
 * change it if you like" rather than as an error that appears when the app
 * cannot decide. Only the ambiguous case (2+ matches, nothing chosen) is
 * styled as needing attention, and only it blocks submit.
 *
 * A second line rather than a column: the grid is already seven day columns
 * wide, and the engagement is an attribute of the customer binding, not an
 * independent axis the eye scans down.
 *
 * It renders nothing at all until the row has both a customer and an activity
 * (`pending`) — there is nothing to resolve against yet, and a prompt on every
 * half-filled row would be noise on the fastest path in the product.
 */
export function TimesheetEngagementCell({
  resolution,
  candidates,
  activeEngagements,
  readOnly = false,
  error,
  open,
  onOpenChange,
  onChange,
}: Props) {
  // Gate 1: no activity (or no customer) — nothing is resolved, nothing shown.
  if (resolution.kind === 'pending') return null;

  const asking = resolution.kind === 'ask';
  const selected = resolution.kind === 'bound' ? resolution.engagement : null;

  // What a BOUND row reads as comes from the shared rule, so this and the
  // approvals row cannot drift on it.
  const display = selected
    ? engagementDisplay({
        engagementId: selected.engagementId,
        engagementName: selected.name,
        coversActivity: selected.coversActivity,
      })
    : null;

  // Matching engagements first, then the rest — one flat list the Combobox
  // sections by contiguous run, so the override is always one scroll away
  // instead of hidden behind a separate affordance.
  const matchingIds = new Set(candidates.map((c) => c.engagementId));
  const others = activeEngagements.filter((e) => !matchingIds.has(e.engagementId));
  const items = [...candidates, ...others];

  // Gate 2: nothing attributed AND nothing on offer — render nothing at all.
  // Announcing "No engagement" put a line on every row of every customer with
  // no live SOW, which is noise: the absence of an engagement is not news, and
  // there is no action to take on it either (the field was a statement, not a
  // control).
  //
  // The two states that DO survive with no engagement bound are not absences.
  // `asking` blocks the submit and is where the answer is given; the unset
  // prompt below is the only entry point for booking time out of scope, which
  // the server explicitly allows. Both have engagements to offer.
  if (display == null && items.length === 0) return null;

  // Three value states, one control:
  //   asking     — no value yet, 2+ could apply. The only state that blocks submit.
  //   display    — a value, either resolved or chosen. `out-of-scope` means the
  //                user attributed it deliberately to an engagement that does
  //                not list the activity, which stays visible rather than
  //                reverting to "not covered".
  //   unset      — nothing matched and nothing chosen; still editable, because
  //                the customer does have live engagements to choose from.
  const outOfScope = display?.tone === 'out-of-scope';
  const label = asking
    ? 'Select engagement'
    : display
      ? display.label
      : 'Not covered — select engagement';

  const title =
    error ??
    (asking
      ? `${candidates.length} engagements cover this entry — pick one before submitting.`
      : display
        ? [selected?.name, selected?.documentNo, outOfScope ? 'does not list this activity' : null]
            .filter(Boolean)
            .join(' · ')
        : 'No engagement covers this activity. Pick one anyway to attribute the time.');

  // Deliberately NOT `TimesheetLabelButton`: that control is sized for a
  // column's primary picker (`min-h-9`, chevron), and this is a sub-label under
  // the customer — borrowing it would add ~36px to every row in the grid. What
  // it does share is the wrapping BLOCK: the row's cell is `whitespace-nowrap`,
  // so an inline sibling of the customer label could never break onto its own
  // line and would overflow into the Activity column.
  //
  // The label itself is `EngagementLabel`, shared with the approvals queue so
  // the two surfaces can't drift on what an engagement looks like. Precedence
  // is the component's, and it matters: ambiguity outranks everything, because
  // an unanswered row is unanswered whatever else is true of it.
  const tone: EngagementTone = asking ? 'ask' : (display?.tone ?? 'unset');
  const content = <EngagementLabel label={label} tone={tone} />;

  // A bound row with no alternative has nothing to change to: a dropdown of one
  // is a dead control, so show it as a plain label.
  const soleChoice = selected != null && items.length <= 1;

  if (open && !readOnly && !soleChoice) {
    // OVERLAID, not swapped in. The Combobox trigger is `h-9`; the label it
    // replaces is ~18px, so rendering it in flow grew the row and shoved every
    // row below it down the moment the picker opened. The label stays mounted
    // (invisible) to hold the row's height, and the control floats above it.
    return (
      <div className="relative mt-0.5">
        {/* Byte-for-byte the closed button's box (`px-2 py-0.5` + the same
            content), just invisible — anything less and the row still changes
            height by the difference when the picker opens. */}
        <div aria-hidden className="invisible px-2 py-0.5">
          {content}
        </div>
        <div className="absolute top-0 left-0 z-30 w-64 max-w-[16rem]">
          <Combobox<EngagementCandidate>
            items={items}
            selectedItem={selected}
            value={selected?.engagementId ?? null}
            defaultOpen
            // Nulling isn't an edit the picker offers: clearing an ambiguous row
            // would just re-raise the same question, and clearing a bound one is
            // done by changing the activity or the customer.
            clearable={false}
            popoverWidth="auto"
            placeholder="Select engagement…"
            hasError={asking}
            getId={(e) => e.engagementId}
            getLabel={(e) => e.name}
            grouping={{
              getKey: (e) => (matchingIds.has(e.engagementId) ? GROUP_MATCHING : GROUP_OTHER),
              // Same header markup as the activity picker's scope sections, so
              // the two dropdowns read as one control family.
              renderHeader: (key) => (
                <div
                  role="presentation"
                  className={cn(
                    'px-3 pb-1 font-semibold text-[10px] text-muted-foreground uppercase tracking-wide',
                    key === GROUP_MATCHING ? 'pt-2' : 'border-t pt-2',
                  )}
                >
                  {key === GROUP_MATCHING ? 'Covers this activity' : 'Other active engagements'}
                </div>
              ),
            }}
            renderItem={(e) => (
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-sm">{e.name}</span>
                <span className="truncate text-muted-foreground text-xs">
                  {[
                    e.documentNo,
                    `${e.effectiveFrom} – ${e.effectiveTo ?? 'open'}`,
                    e.matchedByWildcard ? 'lists no activities' : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
            )}
            onChange={(e) => {
              if (e) onChange(e.engagementId);
              onOpenChange(false);
            }}
            onOpenChange={(next) => {
              if (!next) onOpenChange(false);
            }}
          />
        </div>
      </div>
    );
  }

  if (readOnly || soleChoice) {
    return (
      <div className="mt-0.5 px-2" title={title}>
        {content}
      </div>
    );
  }

  return (
    <div className="mt-0.5">
      <button
        type="button"
        onClick={() => onOpenChange(true)}
        aria-label={selected ? 'Change engagement' : 'Select engagement'}
        title={title}
        className="group/eng flex w-full max-w-full items-center gap-1 rounded-md px-2 py-0.5 text-left transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none"
      >
        {content}
        {/* Advertises editability without making every row look like a live
            dropdown — the same chevron, on the same hover/focus rule, that
            `TimesheetLabelButton` uses on the customer and activity labels. */}
        <ChevronDown
          aria-hidden
          className="size-3 shrink-0 text-muted-foreground/60 opacity-0 transition-opacity group-hover/eng:opacity-100 group-focus-visible/eng:opacity-100"
        />
      </button>
    </div>
  );
}
